import { useEffect, useState } from "react";
import { CustomerService } from "services/CustomerService";

// Shared across lists so each API key name is fetched once per session.
const nameCache = new Map<number, Promise<string>>();

export const forgetApiKeyName = (apiKeyId: number) => nameCache.delete(apiKeyId);

const fetchName = (apiKeyId: number): Promise<string> => {
  let pending = nameCache.get(apiKeyId);
  if (!pending) {
    pending = CustomerService.apiKey
      .getApiKeyById(apiKeyId)
      .then((res) => res.data?.name || `#${apiKeyId}`)
      .catch(() => {
        nameCache.delete(apiKeyId);
        return `#${apiKeyId}`;
      });
    nameCache.set(apiKeyId, pending);
  }
  return pending;
};

// Resolves API key ids shown in company-wide lists to their names.
export const useApiKeyNames = (apiKeyIds: (number | undefined | null)[]): Record<number, string> => {
  const [names, setNames] = useState<Record<number, string>>({});
  const idsKey = Array.from(new Set(apiKeyIds.filter((id): id is number => !!id)))
    .sort((a, b) => a - b)
    .join(",");

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;
    idsKey.split(",").map(Number).forEach((id) => {
      fetchName(id).then((name) => {
        if (!cancelled) setNames((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return names;
};

// Text for an API Key cell: the key's name, its id while the name loads, or "-".
export const apiKeyLabel = (names: Record<number, string>, apiKeyId?: number | null): string =>
  apiKeyId ? names[apiKeyId] ?? `#${apiKeyId}` : "-";
